import React, { useState, useRef } from 'react';

import Tabs from '../components/common/Tab/Tabs';
import ParentConfig from '../components/MasterManage/ParentConfig/ParentConfig';
import ChildConfig from '../components/MasterManage/ChildConfig/ChildConfig';
import Footer from '../components/MasterManage/Footer/Footer';

// Dialog
import { OptionsBase, OptionsSelectList, useMasterOptionsRefresher, useDivisionOptions, useTeamOptions } from '../state/master';
import SettingDialog from '../components/MasterManage/SettingDialog/SettingDialog';
import TeamSettingDialog from '../components/MasterManage/SettingDialog/TeamSettingDialog';
import EnlightIDConnectionCopyDialog from '../components/MasterManage/SettingDialog/EnlightIDConnectionCopyDialog';
import ApproveDialog from '../components/MasterManage/ApproveDialog/ApproveDialog';
import EnlightTeamSettingDialog from '../components/MasterManage/EnlightTeamSettingDialog/EnlightTeamSettingDialog';
import TeamClassficationDialog from '../components/MasterManage/TeamClassficationDialog/TeamClassficationDialog';
import ColumnInitializeDialog, { ColumnInitializeResult } from '../components/MasterManage/SettingDialog/ColumnInitializeDialog';
import { apiClient } from '../lib/apiClient';
import {
  API_ENDPOINTS,
  DIALOG_KEYS,
} from '../constants';
import type { ApproveMember } from '../state/master';
import { shareInFlightRequest } from '../lib/sharedApiRequest';
import { notifyWeeklyReportPublicScopeUpdated } from '../lib/weeklyReportPublicScopeRefresh';
import { saveMasterAndRefresh, getMasterSaveErrorMessage } from '../lib/masterSaveFlow';

import { getCognitoUserAttributes } from '../context/UserContext';
import { useUserProfile, isAdminAuthority } from '../context/UserProfileContext';
import { Alert, Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import './MasterManage.css';
import ManageUsersDialog from '../components/MasterManage/ManageUsersDialog';

const MasterManage: React.FC = () => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const { refreshAllMasterOptions } = useMasterOptionsRefresher();

  // 承認・セレクト関連
  const [division, setDivision] = useState(1);
  const [team, setTeam] = useState(1);
  const [approver, setApprover] = useState('');
  const [approverAM, setApproverAM] = useState('');
  const [reportType, setReportType] = useState('');
  const [project, setProject] = useState('');
  const [powerTrain, setPowerTrain] = useState('');
  const [shimuke, setShimuke] = useState('');
  const [lot, setLot] = useState('');
  const [free1, setFree1] = useState('');
  const [free2, setFree2] = useState('');

  // 親（ParentConfig）の課 初期選択を useUserProfile.department（value, 例: "XQ4"）から自動解決
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();
  const { profile } = useUserProfile();
  const canEdit = isAdminAuthority(profile?.authority);
  const initDivisionDoneRef = useRef(false);

  React.useEffect(() => {
    if (initDivisionDoneRef.current) return;

    const key = String(profile?.department ?? '').trim();
    if (!key) return;

    const norm = (s: any) => String(s ?? '').trim().toUpperCase();
    const target = norm(key);

    const list: any[] = Array.isArray(divisionOptions) ? (divisionOptions as any[]) : [];
    if (!list.length) return;

    // value（例: "XQ4"）または label と一致する課を検索
    const hit = list.find((opt: any) => norm((opt as any)?.value) === target || norm((opt as any)?.label) === target);
    const deptId = Number((hit as any)?.department_id ?? (hit as any)?.id ?? NaN);
    if (!Number.isFinite(deptId) || deptId <= 0) return;

    // 課IDを反映
    setDivision(deptId);

    // チームは同課の先頭を自動選択（なければ 0）
    const teamsForDept = (Array.isArray(teamOptions) ? teamOptions : []).filter(
      (t: any) => Number((t as any)?.department_id) === deptId
    );
    if (teamsForDept.length > 0) {
      const firstTeamId = Number((teamsForDept[0] as any)?.team_id ?? 0);
      setTeam(Number.isFinite(firstTeamId) ? firstTeamId : 0);
    } else {
      setTeam(0);
    }

    initDivisionDoneRef.current = true;
  }, [profile?.department, divisionOptions, teamOptions, setDivision, setTeam]);
  // 選択中の課に対して、チームのプルダウン先頭を自動選択（未選択や不整合時）
  React.useEffect(() => {
    const d = Number(division) || 0;
    const teamList = (Array.isArray(teamOptions) ? teamOptions : []).filter(
      (t: any) => Number((t as any)?.department_id) === d
    );

    if (teamList.length === 0) {
      // 当該課にチームが無ければ未選択(0)
      if (Number(team) !== 0) setTeam(0);
      return;
    }

    // 現在の team が当該課の候補に含まれていない場合は先頭を選択
    const existsInDept = teamList.some((t: any) => Number((t as any)?.team_id) === Number(team));
    if (!existsInDept) {
      const firstTeamId = Number((teamList[0] as any)?.team_id ?? 0);
      setTeam(Number.isFinite(firstTeamId) ? firstTeamId : 0);
    }
  }, [division, teamOptions, team]);

  // Enlight 連携情報（/get_team_docset_list 由来）
  const [enlightTeam, setEnlightTeam] = useState<string | null>(null);
  const [enlightSubteam, setEnlightSubteam] = useState<string | null>(null);
  // TEAM_DOCSET_MANAGE.docset_id（ユーザーが選択・保存した連携先のdocset_id）
  const [enlightDocsetId, setEnlightDocsetId] = useState<number | null>(null);
  const [enlightTeamLoading, setEnlightTeamLoading] = useState<boolean>(false);
  const [teamDocsetData, setTeamDocsetData] = useState<any | null>(null);
  const [teamDocsetError, setTeamDocsetError] = useState<string | null>(null);
  // 画面再レンダリング用キー（強制リマウント）
  const [uiRefreshKey, setUiRefreshKey] = useState(0);
  const [blocking, setBlocking] = useState(false);
  const [saveRefreshWarning, setSaveRefreshWarning] = useState<string | null>(null);
  // 課の保存時にサーバーが重複名へ自動連番を付与した場合の通知
  const [departmentRenameNotice, setDepartmentRenameNotice] = useState<string | null>(null);
  const [manageUsersOpen, setManageUsersOpen] = useState(false);

  // SPA内の画面遷移ではモジュールキャッシュが残るため、マスター管理へ入るたびに
  // サーバーの最新値で再検証する。完了後に子画面を再マウントして初期表示を揃える。
  React.useEffect(() => {
    let active = true;
    setBlocking(true);

    void refreshAllMasterOptions()
      .then(() => {
        if (active) setUiRefreshKey((key) => key + 1);
      })
      .catch((error) => {
        console.error('initial master options refresh error', error);
      })
      .finally(() => {
        if (active) setBlocking(false);
      });

    return () => {
      active = false;
    };
  }, [refreshAllMasterOptions]);

  // 設定ダイアログ
  const [dialogDivision, setDialogDivision] = useState(false);
  const [dialogFreeOption, setDialogFreeOption] = useState(false);
  const [dialogEditColor, setDialogEditColor] = useState(false);
  const [dialogMeta, setDialogMeta] = useState<OptionsBase>();
  const [dialogRows, setDialogRows] = useState<OptionsSelectList[]>();
  // SettingDialog 向けの Enlight 連携情報（呼び出し元から受け取る）
  const [dialogEnlightTeam, setDialogEnlightTeam] = useState<string | null>(null);
  const [dialogEnlightSubteam, setDialogEnlightSubteam] = useState<string | null>(null);
  // API送信用の内部パラメータ（UI表示には使用しない）
  const [dialogEnlightDocsetId, setDialogEnlightDocsetId] = useState<number | null>(null);
  const [dialogTeamDocsetColumns, setDialogTeamDocsetColumns] = useState<any[]>([]);

  // setting dialog
  const [digKey, setDigKey] = useState('');
  const [digOpen, setDigOpen] = useState(false);
  const [digTitle, setDigTitle] = useState('');
  const [digTeam, setDigTeam] = useState<number | undefined>(undefined);
  const [digFilterByTeam, setDigFilterByTeam] = useState<boolean>(false);

  // ApproveDialog
  const [approveDigOpen, setApproveDigOpen] = useState(false);
  const [approveRows, setApproveRows] = useState<ApproveMember[]>([]);
  // EnlightTeamSettingDialog
  const [enlightDigOpen, setEnlightDigOpen] = useState(false);
  const [enlightDialogMode, setEnlightDialogMode] = useState<'team' | 'column'>('team');
  // TeamClassificationDialog
  const [teamClassificationDigOpen, setTeamClassificationDigOpen] = useState(false);
  const [enlightIDConnectionCopyDigOpen, setEnlightIDConnectionCopyDigOpen] = useState(false);
  // ColumnInitializeDialog（共通項目の初期化/他チームからのコピー）
  const [columnInitDigOpen, setColumnInitDigOpen] = useState(false);
  const [columnInitErrorDialog, setColumnInitErrorDialog] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const openSettingDialog = (
    title: string,
    meta: OptionsBase,
    rows: OptionsSelectList[],
    divisionMode?: boolean,
    freeOption?: boolean,
    editColor?: boolean,
    teamOverride?: number,
    dialogFilterByTeam?: boolean,
    enlightTeamArg?: string | null,
    teamDocsetColumnsArg?: any[],
    enlightSubteamArg?: string | null,
    enlightDocsetIdArg?: number | null,
  ) => {
    setDigKey(title)
    setDigTitle(title);

    // 新規フリー項目を開く場合は既存の options を必ず空にする
    const normalizedMeta =
      title === DIALOG_KEYS.NEW_FREE_ITEM
        ? { ...meta, options: [] }
        : meta;

    setDialogMeta(normalizedMeta);
    setDialogRows(rows);
    setDialogDivision((div) => divisionMode ? divisionMode : false);
    setDialogFreeOption((opt) => freeOption ? freeOption : false);
    setDialogEditColor((col) => editColor ? editColor : false);
    setDigTeam(teamOverride);
    setDigFilterByTeam(Boolean(dialogFilterByTeam));
    // 呼び出し元から受け取った Enlight 連携情報（未指定時は既存 state の enlightTeam を利用）
    setDialogEnlightTeam(enlightTeamArg !== undefined ? enlightTeamArg : enlightTeam);
    setDialogEnlightSubteam(enlightSubteamArg !== undefined ? enlightSubteamArg : enlightSubteam);
    setDialogEnlightDocsetId(enlightDocsetIdArg !== undefined ? enlightDocsetIdArg : enlightDocsetId);
    setDialogTeamDocsetColumns(Array.isArray(teamDocsetColumnsArg) ? teamDocsetColumnsArg : []);
    setDigOpen(true);
  };

  // ChildConfig -> ApproveDialog 起動
  const openApproveDialog = (rows: ApproveMember[]) => {
    setApproveRows(rows ?? []);
    setApproveDigOpen(true);
  };

  // 課・チーム選択に応じた Enlight チームID / team_docset 取得（/get_team_docset_list）
  const fetchTeamDocset = React.useCallback(async (cancelledRef?: { current: boolean }) => {
    const normalize = (v: any): string | null => {
      const s = String(v ?? '').trim();
      if (!s) return null;
      const lc = s.toLowerCase();
      if (lc === 'none' || lc === 'null') return null;
      return s;
    };
    const extractEnlightTeam = (data: any): string | null => {
      const arr = Array.isArray(data)
        ? data
        : (Array.isArray((data as any)?.list) ? (data as any).list
          : Array.isArray((data as any)?.data) ? (data as any).data
          : []);
      if (!Array.isArray(arr) || arr.length === 0) {
        const v0 = (data && ((data as any).enlight_team ?? (data as any).enlightTeam)) ?? null;
        return normalize(v0);
      }
      const picked = arr.find((x: any) => String((x as any)?.enlight_team ?? (x as any)?.enlightTeam ?? '').trim() !== '') ?? arr[0];
      const v = (picked as any)?.enlight_team ?? (picked as any)?.enlightTeam ?? null;
      return normalize(v);
    };
    const extractEnlightSubteam = (data: any): string | null => {
      const arr = Array.isArray(data)
        ? data
        : (Array.isArray((data as any)?.list) ? (data as any).list
          : Array.isArray((data as any)?.data) ? (data as any).data
          : []);
      if (!Array.isArray(arr) || arr.length === 0) {
        const v0 = (data && ((data as any).enlight_subteam ?? (data as any).enlightSubteam)) ?? null;
        return normalize(v0);
      }
      const picked = arr.find((x: any) => String((x as any)?.enlight_team ?? (x as any)?.enlightTeam ?? '').trim() !== '') ?? arr[0];
      const v = (picked as any)?.enlight_subteam ?? (picked as any)?.enlightSubteam ?? null;
      return normalize(v);
    };
    const extractTeamDocsetId = (data: any): number | null => {
      const v = (data as any)?.team_docset_id;
      const n = Number(v);
      return v !== null && v !== undefined && Number.isFinite(n) ? n : null;
    };
    setEnlightTeamLoading(true);
    setEnlightTeam(null);
    setEnlightSubteam(null);
    setEnlightDocsetId(null);
    setTeamDocsetData(null);
    setTeamDocsetError(null);
    try {
      const departmentId = Number(division) || 0;
      const teamId = Number(team) || 0;
      const res = await shareInFlightRequest(
        `master-options:team-docset:${departmentId}:${teamId}`,
        () => apiClient.get('/get_team_docset_list', {
          params: {
            department_id: departmentId,
            team_id: teamId,
          },
        }),
      );
      if (cancelledRef?.current) return;
      const data = res?.data ?? null;
      const v = extractEnlightTeam(data);
      const vSub = extractEnlightSubteam(data);
      const vDocsetId = extractTeamDocsetId(data);
      setTeamDocsetData(data);
      setEnlightTeam(v);
      setEnlightSubteam(vSub);
      setEnlightDocsetId(vDocsetId);
      return true;
    } catch {
      if (!cancelledRef?.current) {
        setTeamDocsetData(null);
        setTeamDocsetError('取得に失敗しました');
        setEnlightTeam(null);
        setEnlightSubteam(null);
        setEnlightDocsetId(null);
      }
      return false;
    } finally {
      if (!cancelledRef?.current) setEnlightTeamLoading(false);
    }
  }, [division, team]);

  // ChildConfigには完了待ち用の Promise<void> を渡す。
  // fetchTeamDocset の成功・失敗の戻り値は、親側の再取得判定で引き続き使用する。
  const refreshTeamDocset = React.useCallback(async (): Promise<void> => {
    await fetchTeamDocset();
  }, [fetchTeamDocset]);

  React.useEffect(() => {
    const cancelledRef = { current: false };
    fetchTeamDocset(cancelledRef);
    return () => { cancelledRef.current = true; };
  }, [fetchTeamDocset]);


  React.useEffect(() => {
    if (!canEdit) {
      document.body.classList.add('mastermanage-stable-scrollbar');
      return () => {
        document.body.classList.remove('mastermanage-stable-scrollbar');
      };
    }
    return undefined;
  }, [canEdit]);

  const refreshSavedMasterDisplay = async () => {
    const docsetLoaded = await fetchTeamDocset();
    await refreshAllMasterOptions();
    setUiRefreshKey((value) => value + 1);
    if (docsetLoaded === false) throw new Error('Docset display refresh failed');
  };

  const retrySavedMasterDisplay = async () => {
    setBlocking(true);
    try {
      // Retry reads only. The previous save must not be submitted twice.
      await refreshSavedMasterDisplay();
      setSaveRefreshWarning(null);
    } catch {
      setSaveRefreshWarning('保存は完了していますが、表示の再取得に失敗しました。通信状態を確認してください。');
    } finally {
      setBlocking(false);
    }
  };

  const ActiveSettingDialog = digKey === DIALOG_KEYS.TEAM ? TeamSettingDialog : SettingDialog;

  return (
    <div className="">
      {!canEdit && (
        <div className="mastermanage-readonly-overlay" role="dialog" aria-live="polite" aria-modal="false">
          <div className="mastermanage-readonly-box">
            <div className="mastermanage-readonly-title">課別設定の変更は各課の管理メンバーにご相談ください。</div>
            <div className="mastermanage-readonly-link">
              <button
                type="button"
                onClick={() => setManageUsersOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  color: isDark ? '#60a5fa' : '#1976d2',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                管理メンバー名簿
              </button>
            </div>
          </div>
        </div>
      )}
      <Tabs />
      {departmentRenameNotice && (
        <Alert
          severity="info"
          sx={{ mx: 2, my: 1 }}
          onClose={() => setDepartmentRenameNotice(null)}
        >
          {departmentRenameNotice}
        </Alert>
      )}
      {saveRefreshWarning && (
        <Alert
          severity="warning"
          sx={{ mx: 2, my: 1 }}
          action={<Button disabled={blocking} onClick={retrySavedMasterDisplay}>表示を再取得</Button>}
        >
          {saveRefreshWarning}
        </Alert>
      )}
      <ParentConfig
        key={`pc-${uiRefreshKey}`}
        division={division}
        setDivision={setDivision}
        team={team}
        setTeam={setTeam}
        openSettingDialog={openSettingDialog}
        enlightTeam={enlightTeam}
        enlightSubteam={enlightSubteam}
        enlightTeamLoading={enlightTeamLoading}
        teamDocsetData={teamDocsetData}
        openEnlightTeamSettingDialog={() => { setEnlightDialogMode('team'); setEnlightDigOpen(true); }}
        openTeamClassificationDialog={() => setTeamClassificationDigOpen(true)}
        openEnlightIDConnectionCopyDialog={() => setEnlightIDConnectionCopyDigOpen(true)}
      />
      <ChildConfig
        key={`cc-${uiRefreshKey}`}
        division={division}
        team={team}
        enlightTeam={enlightTeam}
        enlightSubteam={enlightSubteam}
        enlightDocsetId={enlightDocsetId}
        teamDocsetData={teamDocsetData}
        teamDocsetDataLoading={enlightTeamLoading}
        teamDocsetDataError={teamDocsetError}
        refreshTeamDocset={refreshTeamDocset}
        reportType={reportType}
        setReportType={setReportType}
        project={project}
        setProject={setProject}
        approver={approver}
        setApprover={setApprover}
        approverAM={approverAM}
        setApproverAM={setApproverAM}
        powerTrain={powerTrain}
        setPowerTrain={setPowerTrain}
        shimuke={shimuke}
        setShimuke={setShimuke}
        lot={lot}
        setLot={setLot}
        free1={free1}
        setFree1={setFree1}
        free2={free2}
        setFree2={setFree2}
        openSettingDialog={openSettingDialog}
        openApproveDialog={openApproveDialog}
        openEnlightColumnDialog={() => { setEnlightDialogMode('column'); setEnlightDigOpen(true); }}
        openColumnInitializeDialog={() => setColumnInitDigOpen(true)}
        canEdit={canEdit}
      />
      <Footer />
      <ActiveSettingDialog
          key={`sd-${digKey}-${dialogRows?.length ?? 0}`}
          setting_id={dialogMeta?.attr_id ?? -1}
          title={digTitle}
          division={division}
          team={digTeam ?? team}
          meta={dialogMeta}
          rows={dialogRows}
          dialogDivision={dialogDivision}
          dialogFreeOption={dialogFreeOption}
          dialogEditColor={dialogEditColor}
          dialogClassification={dialogEditColor}
          dialogFilterByTeam={digFilterByTeam}
          open={digOpen}
          digKey={digKey}
          enlightTeam={dialogEnlightTeam ?? enlightTeam}
          enlightSubteam={dialogEnlightSubteam ?? enlightSubteam}
          teamDocsetColumns={dialogTeamDocsetColumns}
          canEdit={canEdit}
          onAccept={async (newMeta: OptionsBase) => {
          if (digKey === DIALOG_KEYS.NEW_FREE_ITEM && newMeta) {
            // 新規フリー項目の作成時は add_free_attribute にPOST
            try {
              // Cognito から email を取得
              const attrs = await getCognitoUserAttributes();
              const email = (attrs?.email as string) ?? '';

              {
                const et = String((dialogEnlightTeam ?? enlightTeam) ?? '').trim();
                const est = String((dialogEnlightSubteam ?? enlightSubteam) ?? '').trim();
                // API送信用の内部パラメータ（UI表示には使用しない）
                const edid = dialogEnlightDocsetId ?? enlightDocsetId;
                const payload: any = {
                  department_id: division,
                  team_id: team,
                  attr_name: newMeta.label,
                  // attr_required は共通項目/フリー項目の区分。フリー項目は常に 0。
                  attr_required: 0,
                  // 入力必須設定は input_required に保存する。
                  input_required: ((newMeta as any).input_required ?? newMeta.required) ? 1 : 0,
                  attr_type: newMeta.attr_type,
                  multiple_select: newMeta.multiple_select,
                  min_length: newMeta.min_length,
                  max_length: newMeta.max_length,
                  use_for_sort: (newMeta as any).use_for_sort ? 1 : 0,
                  ...(et ? { enlight_team: et } : {}),
                  ...(et && est ? { enlight_subteam: est } : {}),
                  ...(et && edid !== null && edid !== undefined ? { docset_id: edid } : {}),
                  ...(et ? {
                    enlight_column: (() => {
                      const ec: any = (newMeta as any)?.enlight_column;
                      const attrIdNum = Number((newMeta as any)?.attr_id);
                      if (!ec || !ec.enlight_column_name) {
                        return { attr_id: attrIdNum };
                      }
                      return {
                        attr_id: attrIdNum,
                        enlight_column_name: String(ec.enlight_column_name),
                        enlight_dtype: String(ec.enlight_dtype ?? '')
                      };
                    })()
                  } : {}),
                  email: email
                };
                if ([11, 12, 13].includes(Number(newMeta.attr_type))) {
                  (payload as any).new_params = (newMeta.options ?? []).map((opt: any) => ({
                    ...opt,
                    ...(typeof opt?.attr_id === 'number' ? { attr_id: Number(opt.attr_id) } : {}),
                  }));
                }
                setBlocking(true);
                setDigOpen(false);
                await apiClient.post(API_ENDPOINTS.ADD_FREE_ATTRIBUTE, payload);
              }
              setBlocking(true);
              try {
                await fetchTeamDocset();
                await refreshAllMasterOptions();
                setUiRefreshKey((k) => k + 1);
              } finally {
                setBlocking(false);
              }
            } catch (e) {
              console.error("add_free_attribute post error", e);
              setBlocking(false);
            }
          } else if (digKey === DIALOG_KEYS.DIVISION && newMeta) {
            // 課変更の時は refresh_departments にPOST
            // 失敗時はダイアログを閉じずにエラーを表示する（入力内容を保持）
            try {
              setBlocking(true);
              setSaveRefreshWarning(null);
              setDepartmentRenameNotice(null);
              let renamed: Array<{ from: string; to: string }> = [];
              const outcome = await saveMasterAndRefresh(
                async () => {
                  const res = await apiClient.post(API_ENDPOINTS.REFRESH_DEPARTMENTS, {
                    // 本文最大文字数（課）
                    ...(typeof (newMeta as any)?.department_body_max_length === 'number'
                      ? { department_body_max_length: (newMeta as any).department_body_max_length }
                      : {}),
                    new_params: newMeta.options
                      ? newMeta.options.map((opt: OptionsSelectList) => ({
                          value: opt.value,
                          status: (opt as any).status ?? 'Y',
                          public: (opt as any).public,
                          enlight_sync_default: (opt as any).enlight_sync_default,
                          ...(typeof (opt as any).department_id === 'number'
                            ? { department_id: Number((opt as any).department_id) }
                            : {}),
                        }))
                      : []
                  });
                  const list = (res as any)?.data?.renamed;
                  renamed = Array.isArray(list) ? list : [];
                },
                async () => {
                  // サーバ更新完了後にマスタ再取得をトリガー
                  await refreshAllMasterOptions();
                  setUiRefreshKey((k) => k + 1);
                },
              );
              if (renamed.length > 0) {
                setDepartmentRenameNotice(
                  `課名が重複していたため、自動で連番を付与しました: ${renamed.map((r) => `${r.from} → ${r.to}`).join('、')}`
                );
              }
              if (outcome.refreshFailed) {
                console.error('master display refresh failed after successful save', outcome.refreshError);
                setSaveRefreshWarning('設定の保存は完了しましたが、画面の最新化に失敗しました。再度保存せず「表示を再取得」を押してください。');
              }
            } catch (e) {
              console.error("refresh_departments post error", e);
              if ((e as any)?.response?.status === 409) {
                throw new Error('課名（課コード）が既存の課と重複しているため保存できませんでした。入力内容を確認してください。');
              }
              throw new Error(getMasterSaveErrorMessage(e));
            } finally {
              setBlocking(false);
            }
          } else if (digKey === DIALOG_KEYS.TEAM && newMeta) {
            // チーム変更の時は refresh_department_teams にPOST
            try {
              setBlocking(true);
              setDigOpen(false);
              const normalizeTeamName = (raw: any): string =>
                String(raw ?? '').trim().toLowerCase();
              const normalizeEnlightTeam = (raw: any): string => {
                const s = String(raw ?? '').trim();
                if (!s) return '';
                const lc = s.toLowerCase();
                if (lc === 'none' || lc === 'null') return '';
                return s;
              };
              const extractDocsetSummary = (data: any): { enlightTeam: string; docsetId: string | number | null } => {
                const arr = Array.isArray(data)
                  ? data
                  : Array.isArray((data as any)?.list)
                    ? (data as any).list
                    : Array.isArray((data as any)?.data)
                      ? (data as any).data
                      : data
                        ? [data]
                        : [];
                const pick = Array.isArray(arr)
                  ? arr.find((x: any) => normalizeEnlightTeam((x as any)?.enlight_team ?? (x as any)?.enlightTeam)) ?? arr[0]
                  : null;
                const enlightTeamValue = normalizeEnlightTeam(
                  (pick as any)?.enlight_team ??
                  (pick as any)?.enlightTeam ??
                  (data as any)?.enlight_team ??
                  (data as any)?.enlightTeam
                );
                const rawDocsetId =
                  (pick as any)?.docset_id ??
                  (pick as any)?.docsetId ??
                  (data as any)?.docset_id ??
                  (data as any)?.docsetId;
                const docsetId = rawDocsetId == null || String(rawDocsetId).trim() === '' ? null : rawDocsetId;
                return { enlightTeam: enlightTeamValue, docsetId };
              };
              const existingTeamIdsBeforeUpdate = new Set<number>(
                (Array.isArray(teamOptions) ? teamOptions : [])
                  .filter((t: any) => Number((t as any)?.department_id) === Number(division))
                  .map((t: any) => Number((t as any)?.team_id))
                  .filter((id: number) => Number.isFinite(id) && id > 0)
              );
              const rawTeamOptions = (newMeta.options ?? [])
                .filter((opt: OptionsSelectList) => opt.department_id === division);
              const namesHavingNewRows = new Set(
                rawTeamOptions
                  .filter((opt: any) =>
                    typeof (opt as any)?.team_id !== 'number' &&
                    String((opt as any)?.status ?? 'Y').toUpperCase() !== 'N'
                  )
                  .map((opt: any) => normalizeTeamName((opt as any)?.value ?? (opt as any)?.label))
                  .filter((name: string) => name.length > 0)
              );
              const payloadTeamOptions = rawTeamOptions.filter((opt: any) => {
                const hasTeamId = typeof (opt as any)?.team_id === 'number';
                const isDisabled = String((opt as any)?.status ?? 'Y').toUpperCase() === 'N';
                const name = normalizeTeamName((opt as any)?.value ?? (opt as any)?.label);
                if (!name) return true;
                if (hasTeamId && isDisabled && namesHavingNewRows.has(name)) return false;
                return true;
              });
              const copyEnlightRequests = payloadTeamOptions
                .map((opt: any) => {
                  const shouldCopy = Boolean((opt as any)?.copy_same_enlight_connection);
                  if (!shouldCopy) return null;
                  const usingDepartment = Number((opt as any)?.using_department ?? 0);
                  const sourceTeamId = Number((opt as any)?.using_team ?? 0);
                  if (usingDepartment !== Number(division) || !Number.isFinite(sourceTeamId) || sourceTeamId <= 0) return null;
                  const status = String((opt as any)?.status ?? 'Y').toUpperCase();
                  if (status === 'N') return null;
                  const targetTeamId = Number((opt as any)?.team_id ?? 0);
                  const targetTeamName = String((opt as any)?.value ?? (opt as any)?.label ?? '').trim();
                  if (!targetTeamName) return null;
                  return {
                    sourceTeamId,
                    targetTeamId: Number.isFinite(targetTeamId) && targetTeamId > 0 ? targetTeamId : null,
                    targetTeamNameNorm: normalizeTeamName(targetTeamName),
                  };
                })
                .filter((x): x is { sourceTeamId: number; targetTeamId: number | null; targetTeamNameNorm: string } => Boolean(x));

              await apiClient.post(API_ENDPOINTS.REFRESH_DEPARTMENT_TEAMS, {
                department_id: division,
                // 本文最大文字数（課）
                ...(typeof (newMeta as any)?.department_body_max_length === 'number'
                  ? { department_body_max_length: (newMeta as any).department_body_max_length }
                  : {}),
                new_params: payloadTeamOptions
                  ? payloadTeamOptions
                      .map((opt: OptionsSelectList) => ({
                        value: opt.value,
                        status: (opt as any).status ?? 'Y',
                        public: (opt as any).public,
                        ...(typeof (opt as any)?.other_setting_using === 'boolean'
                          ? { other_setting_using: Boolean((opt as any).other_setting_using) }
                          : {}),
                        ...(typeof (opt as any)?.using_department === 'number'
                          ? { using_department: Number((opt as any).using_department) }
                          : {}),
                        ...(typeof (opt as any)?.using_team === 'number'
                          ? { using_team: Number((opt as any).using_team) }
                          : {}),
                        ...(Boolean((opt as any)?.copy_same_enlight_connection)
                          ? { copy_same_enlight_connection: true }
                          : {}),
                        ...(typeof (opt as any).team_id === 'number'
                          ? { team_id: Number((opt as any).team_id) }
                          : {}),
                        ...(typeof (opt as any)?.team_setting_mode === 'string'
                          ? { team_setting_mode: String((opt as any).team_setting_mode) }
                          : {}),
                        ...(() => {
                          const n = Number((opt as any)?.order_number);
                          return Number.isFinite(n) ? { order_number: n } : {};
                        })(),
                        start_fiscal_year: (() => {
                          const v = (opt as any)?.start_fiscal_year ?? (opt as any)?.team_start_fiscal_year;
                          return typeof v === 'number' ? Number(v) : null;
                        })(),
                        end_fiscal_year: (() => {
                          const v = (opt as any)?.end_fiscal_year ?? (opt as any)?.team_fiscal_year;
                          return typeof v === 'number' ? Number(v) : null;
                        })(),
                        team_body_max_length: (typeof (opt as any)?.team_body_max_length === 'number'
                          ? (opt as any).team_body_max_length
                          : null),
                      }))
                  : []
              });
              // サーバ更新完了後にマスタ再取得をトリガー
              setBlocking(true);
              try {
                await refreshAllMasterOptions();
                setUiRefreshKey((k) => k + 1);

                if (copyEnlightRequests.length > 0) {
                  let latestTeamRows: any[] = [];
                  try {
                    const latestTeamRes = await apiClient.get(API_ENDPOINTS.GET_ALL_TEAMS);
                    latestTeamRows = Array.isArray(latestTeamRes?.data)
                      ? latestTeamRes.data
                      : Array.isArray((latestTeamRes?.data as any)?.list)
                        ? (latestTeamRes?.data as any).list
                        : Array.isArray((latestTeamRes?.data as any)?.data)
                          ? (latestTeamRes?.data as any).data
                          : [];
                  } catch (e) {
                    console.warn('get_all_teams failed after refresh_department_teams', e);
                  }
                  const usedTargetTeamIds = new Set<number>();
                  const resolveTargetTeamId = (req: { targetTeamId: number | null; targetTeamNameNorm: string }): number | null => {
                    if (req.targetTeamId && req.targetTeamId > 0) return req.targetTeamId;
                    const candidates = (Array.isArray(latestTeamRows) ? latestTeamRows : [])
                      .filter((t: any) => Number((t as any)?.department_id) === Number(division))
                      .map((t: any) => ({
                        id: Number((t as any)?.team_id ?? 0),
                        nameNorm: normalizeTeamName((t as any)?.label ?? (t as any)?.value),
                      }))
                      .filter((t: { id: number; nameNorm: string }) => t.id > 0 && t.nameNorm === req.targetTeamNameNorm);
                    if (candidates.length === 0) return null;
                    const preferred = candidates.find((c) => !existingTeamIdsBeforeUpdate.has(c.id) && !usedTargetTeamIds.has(c.id)) ?? candidates[0];
                    return preferred?.id ?? null;
                  };

                  const sourceDocsetCache = new Map<number, { enlightTeam: string; docsetId: string | number | null }>();
                  const targetIdsBySource = new Map<number, number[]>();

                  for (const req of copyEnlightRequests) {
                    const targetId = resolveTargetTeamId(req);
                    if (!targetId || targetId <= 0) continue;
                    usedTargetTeamIds.add(targetId);
                    const list = targetIdsBySource.get(req.sourceTeamId) ?? [];
                    if (!list.includes(targetId)) list.push(targetId);
                    targetIdsBySource.set(req.sourceTeamId, list);
                  }

                  const copyQueue: Array<{ sourceTeamId: number; targetTeamIds: number[] }> = [];
                  targetIdsBySource.forEach((targetTeamIds, sourceTeamId) => {
                    copyQueue.push({ sourceTeamId, targetTeamIds });
                  });

                  for (let i = 0; i < copyQueue.length; i++) {
                    const sourceTeamId = Number(copyQueue[i].sourceTeamId);
                    const targetTeamIds = copyQueue[i].targetTeamIds;
                    if (targetTeamIds.length === 0) continue;
                    let sourceSummary = sourceDocsetCache.get(sourceTeamId);
                    if (!sourceSummary) {
                      try {
                        const resDocset = await apiClient.get('/get_team_docset_list', {
                          params: {
                            department_id: Number(division) || 0,
                            team_id: Number(sourceTeamId) || 0,
                          },
                        });
                        sourceSummary = extractDocsetSummary(resDocset?.data);
                        sourceDocsetCache.set(sourceTeamId, sourceSummary);
                      } catch (e) {
                        console.warn('get_team_docset_list failed before copy_team_docset', { division, sourceTeamId, e });
                        continue;
                      }
                    }
                    if (!sourceSummary || !sourceSummary.enlightTeam) continue;

                    try {
                      await apiClient.post('/copy_team_docset', {
                        department_id: Number(division) || 0,
                        source_team_id: Number(sourceTeamId) || 0,
                        source_enlight_team: sourceSummary.enlightTeam,
                        source_docset_id: sourceSummary.docsetId,
                        target_team_ids: targetTeamIds,
                      });
                    } catch (e) {
                      console.warn('copy_team_docset failed after team refresh', {
                        division,
                        sourceTeamId,
                        targetTeamIds,
                        e,
                      });
                    }
                  }
                }
              } finally {
                setBlocking(false);
              }
            } catch (e) {
              console.error("refresh_department_teams post error", e);
              setBlocking(false);
            }
          } else if (!dialogDivision) {
            // ChildConfig から呼ばれたダイアログ（divisionMode=false）の場合
            // 属性変更となるので refresh_attributes にPOST
            try {
              const includeColorInParams =
                Boolean((newMeta as any)?.set_color) ||
                Array.isArray(newMeta?.options) &&
                  (newMeta.options as any[]).some((opt: any) => String(opt?.color ?? '').trim().length > 0);
              // enlight_team を付与。未設定の場合は enlight_column を送信しない
              const et = String((dialogEnlightTeam ?? enlightTeam) ?? '').trim();
              const est = String((dialogEnlightSubteam ?? enlightSubteam) ?? '').trim();
              // API送信用の内部パラメータ（UI表示には使用しない）
              const edid = dialogEnlightDocsetId ?? enlightDocsetId;
              setBlocking(true);
              setSaveRefreshWarning(null);
              const outcome = await saveMasterAndRefresh(
                () => apiClient.post(API_ENDPOINTS.REFRESH_ATTRIBUTES, {
                  attr_id: newMeta.attr_id,
                  department_id: division,
                  team_id: team,
                  attr_name: newMeta.label,
                  attr_type: newMeta.attr_type,
                   min_length: newMeta.min_length,
                   max_length: newMeta.max_length,
                   multiple_select: newMeta.multiple_select,
                   input_required: (newMeta as any).input_required ?? newMeta.required,
                   use_for_sort: (newMeta as any).use_for_sort ? 1 : 0,
                   visible: newMeta.visible,
                   ...(et ? { enlight_team: et } : {}),
                   ...(et && est ? { enlight_subteam: est } : {}),
                   ...(et && edid !== null && edid !== undefined ? { docset_id: edid } : {}),
                   ...(et ? {
                     enlight_column: (() => {
                       const ec: any = (newMeta as any)?.enlight_column;
                      const attrIdNum = Number((newMeta as any)?.attr_id);
                      if (!ec || !ec.enlight_column_name) {
                        // 未選択時は { attr_id } のみ
                        return { attr_id: attrIdNum };
                      }
                      return {
                        attr_id: attrIdNum,
                        enlight_column_name: String(ec.enlight_column_name),
                        enlight_dtype: String(ec.enlight_dtype ?? '')
                      };
                    })()
                  } : {}),
                  new_params: newMeta.options
                    ? newMeta.options
                        .filter((opt: OptionsSelectList) =>
                          Number(opt.department_id) === Number(division) &&
                          Number(opt.team_id) === Number(team)
                        )
                        .map((opt: OptionsSelectList) => ({
                          value: opt.value,
                          status: (opt as any).status ?? 'Y',
                          public: (opt as any).public,
                          ...(includeColorInParams ? { color: (opt as any).color ?? '' } : {}),
                          ...(typeof (opt as any).option_id === 'number'
                            ? { option_id: Number((opt as any).option_id) }
                            : {}),
                        }))
                    : []
                }),
                async () => {
                  // 週報分類に限らず、別タブで開いたままの入力画面が最新マスタへ入れ替わるよう通知する。
                  notifyWeeklyReportPublicScopeUpdated();
                  await refreshSavedMasterDisplay();
                },
              );
              if (outcome.refreshFailed) {
                console.error('master display refresh failed after successful save', outcome.refreshError);
                setSaveRefreshWarning('設定の保存は完了しましたが、画面の最新化に失敗しました。再度保存せず「表示を再取得」を押してください。');
              }
            } catch (e) {
              console.error("refresh_attributes post error", e);
              throw new Error(getMasterSaveErrorMessage(e));
            } finally {
              setBlocking(false);
            }
          }
          setDigOpen(false);
        }}
        onClose={() => setDigOpen(false)}
      />

      <EnlightTeamSettingDialog
        open={enlightDigOpen}
        division={division}
        team={team}
        enlight_team={enlightTeam}
        enlight_subteam={enlightSubteam}
        enlight_docset_id={enlightDocsetId}
        initialMode={enlightDialogMode}
        canEdit={canEdit}
        onClose={() => setEnlightDigOpen(false)}
        onAcceptTeam={(payload) => {
          // チーム連携設定後はダイアログを閉じず、続けてカラム連携設定へ進むためステートのみ更新
          setEnlightTeam(payload.enlight_team ? payload.enlight_team : null);
          setEnlightSubteam(payload.enlight_subteam ?? null);
          setEnlightDocsetId(payload.docset_id ?? null);
          // ChildConfig の連携状態表示を即時反映するため、team_docset を再取得
          void fetchTeamDocset();
        }}
        onAcceptColumn={async (payload) => {
          // カラム連携確定後はマスタ全体を再読み込みし、画面を強制再レンダリング
          console.info('Column binding accepted', payload);
          setEnlightDigOpen(false);
          await fetchTeamDocset();
          setBlocking(true);
          try {
            await refreshAllMasterOptions();
            setUiRefreshKey((k) => k + 1);
          } catch (e) {
            console.error('refreshAllMasterOptions error after column binding', e);
          } finally {
            setBlocking(false);
          }
        }}
        onAccept={() => { setEnlightDigOpen(false); }}
      />

      <ApproveDialog
        open={approveDigOpen}
        division={division}
        team={team}
        rows={approveRows}
        canEdit={canEdit}
        onClose={() => setApproveDigOpen(false)}
        onAccept={async (rows: ApproveMember[]) => {
          try {
            setBlocking(true);
            setApproveDigOpen(false);
            await apiClient.post(API_ENDPOINTS.REFRESH_MANAGE_MEMBERS, {
              department_id: division,
              team_id: 0,
              new_params: (rows ?? []).map((r) => ({
                department_id: Number(r.department_id ?? division),
                team_id: 0,
                name: String(r.name ?? '').trim(),
                approve_3: Number(r.approve_3) === 1 ? 1 : 0,
                approve_am: Number(r.approve_am) === 1 ? 1 : 0,
              })),
            });
            // マスタ更新トリガー（承認者一覧も refreshMasterOptionsState に紐づく）
            setBlocking(true);
            try {
              await refreshAllMasterOptions();
            } finally {
              setBlocking(false);
            }
          } catch (e) {
            console.error('refresh_manage_members post error', e);
          }
          setApproveDigOpen(false);
        }}
      />
      <ManageUsersDialog open={manageUsersOpen} onClose={() => setManageUsersOpen(false)} />
      <TeamClassficationDialog
        open={teamClassificationDigOpen}
        division={division}
        canEdit={canEdit}
        onClose={() => setTeamClassificationDigOpen(false)}
        onSaved={async () => {
          setBlocking(true);
          try {
            await refreshAllMasterOptions();
            setUiRefreshKey((k) => k + 1);
          } finally {
            setBlocking(false);
          }
        }}
      />
      <EnlightIDConnectionCopyDialog
        open={enlightIDConnectionCopyDigOpen}
        division={division}
        canEdit={canEdit}
        onClose={() => setEnlightIDConnectionCopyDigOpen(false)}
      />
      <ColumnInitializeDialog
        open={columnInitDigOpen}
        division={division}
        team={team}
        canEdit={canEdit}
        onClose={() => setColumnInitDigOpen(false)}
        onSubmit={async (result: ColumnInitializeResult) => {
          try {
            setBlocking(true);
            setColumnInitDigOpen(false);
            const res = await apiClient.post(API_ENDPOINTS.RESET_TEAM_COLUMNS, {
              department_id: division,
              team_id: team,
              mode: result.mode,
              reset: result.reset,
              ...(result.mode === 'copy'
                ? {
                    copy_department_id: result.copyDivisionId,
                    copy_team_id: result.copyTeamId,
                    copy_same_enlight_connection: result.copySameEnlightConnection,
                  }
                : {}),
            });
            const data: any = res?.data;
            if (!data || data.ok === false || data.error) {
              console.error('reset_team_columns failed', data);
              setColumnInitErrorDialog({
                open: true,
                message:
                  data?.error ||
                  data?.result?.error_message ||
                  'マスタ設定の再設定に失敗しました。内容を確認のうえ、再度お試しください。',
              });
              return;
            }
            await fetchTeamDocset();
            await refreshAllMasterOptions();
            setUiRefreshKey((k) => k + 1);
          } catch (e) {
            console.error('reset_team_columns post error', e);
            setColumnInitErrorDialog({
              open: true,
              message: 'マスタ設定の再設定に失敗しました。通信エラーが発生しました。',
            });
          } finally {
            setBlocking(false);
          }
        }}
      />
      <Dialog
        open={columnInitErrorDialog.open}
        onClose={() => setColumnInitErrorDialog({ open: false, message: '' })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>エラー</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{columnInitErrorDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnInitErrorDialog({ open: false, message: '' })} variant="outlined">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={blocking}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default MasterManage;